/**
 * The Pen: a shape placed point by point, each exactly where it belongs. What it takes from the
 * editor (the finger, the loupe, the plan's corners and lines) is the editor's; here is what is the
 * Pen's own — the path so far as the shape it will be, and the guides a point settles on where the
 * plan has nothing to offer: square to the plan, square to the side before it, in line with a point
 * already placed (as Keynote's and Figma's smart guides are).
 */
import type { Vec } from '../../shared/doc.ts';
import type { PointKind, PolyForm } from './form.ts';

/** What the next point is: a corner, a rounded corner, a point the outline curves through — or the finger draws the outline itself. */
export type PenKind = PointKind | 'draw';

/** The points placed so far. */
export interface PenPath {
  pts: readonly Vec[];
  round: readonly number[];
  r: readonly number[];
}

/**
 * The path as the shape it will be, with a point being placed (`at`, after the last) or one of its
 * points being moved (`index`) — null while it has fewer than three points.
 */
export function penForm(path: PenPath, pending: { at: Vec; index: number | null; kind: PointKind; r: number } | null = null): PolyForm | null {
  const pts = path.pts.slice();
  const round = path.round.slice();
  const r = path.r.slice();
  if (pending && pending.index !== null) pts[pending.index] = pending.at;
  else if (pending) {
    pts.push(pending.at);
    round.push(pending.kind === 'curve' ? 1 : 0);
    r.push(pending.kind === 'rounded' ? pending.r : 0);
  }
  if (pts.length < 3) return null;
  const f: PolyForm = { f: 'poly', pts: pts.flatMap((p) => [p.x, p.y]), round };
  if (r.some((x, i) => x > 0 && !round[i])) f.r = r.map((x, i) => (round[i] ? 0 : x));
  return f;
}

/** A guide a point may settle on: the line through `o` along `u` (unit). `square`: it stands square to the side before it at `o`. */
export interface Guide {
  o: Vec;
  u: Vec;
  square: boolean;
}

/** A point settled on guides: where, and the one or two it lies on. */
export interface GuideSnap {
  p: Vec;
  guides: Guide[];
}

const DEG = Math.PI / 180;
const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
const unit = (a: Vec): Vec => {
  const l = Math.hypot(a.x, a.y) || 1;
  return { x: a.x / l, y: a.y / l };
};

/**
 * The guides for a point with `prev` before it and `next` after it (the side back to the first
 * point, for the one being placed): square to the plan (across, down and at 45°) from either; square
 * to, or on in line with, the side before `prev` and the side after `next`; and across and down
 * through every point placed (in line with it).
 */
export function penGuides(pts: readonly Vec[], prev: Vec | null, prevPrev: Vec | null, next: Vec | null, nextNext: Vec | null): Guide[] {
  const out: Guide[] = [];
  const axes = [0, 45, 90, 135].map((a) => ({ x: Math.cos(a * DEG), y: Math.sin(a * DEG) }));
  const from = (o: Vec | null, before: Vec | null) => {
    if (!o) return;
    for (const u of axes) out.push({ o, u, square: false });
    if (before && Math.hypot(o.x - before.x, o.y - before.y) > 1e-6) {
      const d = unit(sub(o, before));
      out.push({ o, u: d, square: false });
      out.push({ o, u: { x: -d.y, y: d.x }, square: true });
    }
  };
  from(prev, prevPrev);
  from(next, nextNext);
  for (const q of pts) {
    if (q === prev || q === next) continue;
    out.push({ o: q, u: { x: 1, y: 0 }, square: false }, { o: q, u: { x: 0, y: 1 }, square: false });
  }
  return out;
}

/**
 * Where q settles on the guides within `reach` (image px): where two of them cross, if that is in
 * reach (a rectangle's last corner, square to both its neighbours), else the nearest one. Null:
 * none in reach.
 */
export function snapToGuides(q: Vec, guides: readonly Guide[], reach: number): GuideSnap | null {
  const near: { g: Guide; p: Vec; d: number }[] = [];
  for (const g of guides) {
    const t = dot(sub(q, g.o), g.u);
    const p = { x: g.o.x + g.u.x * t, y: g.o.y + g.u.y * t };
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d <= reach) near.push({ g, p, d });
  }
  if (!near.length) return null;
  let best: GuideSnap | null = null;
  let bd = Infinity;
  for (let i = 0; i < near.length; i++) {
    for (let j = i + 1; j < near.length; j++) {
      const a = near[i]!.g,
        b = near[j]!.g;
      // two from one point cross only at that point, where nothing new is placed
      if (a.o === b.o) continue;
      const c = cross(a.u, b.u);
      if (Math.abs(c) < Math.sin(20 * DEG)) continue;
      const t = cross(sub(b.o, a.o), b.u) / c;
      const x = { x: a.o.x + a.u.x * t, y: a.o.y + a.u.y * t };
      const d = Math.hypot(q.x - x.x, q.y - x.y);
      // two guides crossing win over one, a squared one over one that is merely in line
      const score = d - (a.square || b.square ? 0.25 * reach : 0);
      if (d <= reach && score < bd) {
        bd = score;
        best = { p: x, guides: [a, b] };
      }
    }
  }
  if (best) return best;
  near.sort((a, b) => a.d - (a.g.square ? 0.25 * reach : 0) - (b.d - (b.g.square ? 0.25 * reach : 0)));
  return { p: near[0]!.p, guides: [near[0]!.g] };
}
